import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Download,
  ExternalLink,
  FolderPlus,
  MoreHorizontal,
  Loader2,
} from "lucide-react";
import { EmailAttachment } from "@/hooks/useEmailAttachments";
import { DocumentViewerDialog, type ViewableDocument } from "@/components/DocumentViewerDialog";
import { triggerDownload } from "@/lib/documentViewerUtils";

interface EmailAttachmentItemProps {
  attachment: EmailAttachment;
  onGetDownloadUrl: (storagePath: string) => Promise<string | null>;
  onSaveToDocuments: (attachment: EmailAttachment) => void;
  isSaving?: boolean;
}

// Get icon based on content type
const getFileIcon = (contentType: string, filename: string) => {
  if (contentType.startsWith("image/")) {
    return <Image className="h-4 w-4 text-green-600" />;
  }
  if (contentType === "application/pdf") {
    return <FileText className="h-4 w-4 text-red-600" />;
  }
  if (
    contentType.includes("spreadsheet") ||
    contentType.includes("excel") ||
    filename.endsWith(".xlsx") ||
    filename.endsWith(".xls") ||
    filename.endsWith(".csv")
  ) {
    return <FileSpreadsheet className="h-4 w-4 text-green-700" />;
  }
  if (
    contentType.includes("document") ||
    contentType.includes("word") ||
    filename.endsWith(".docx") ||
    filename.endsWith(".doc")
  ) {
    return <FileText className="h-4 w-4 text-blue-600" />;
  }
  return <File className="h-4 w-4 text-muted-foreground" />;
};

// Format file size for display
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const EmailAttachmentItem = ({
  attachment,
  onGetDownloadUrl,
  onSaveToDocuments,
  isSaving,
}: EmailAttachmentItemProps) => {
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [viewing, setViewing] = useState<ViewableDocument | null>(null);

  const handleDownload = async () => {
    setIsLoadingUrl(true);
    try {
      const url = await onGetDownloadUrl(attachment.storage_path);
      if (url) triggerDownload(url, attachment.filename);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const handleView = async () => {
    setIsLoadingUrl(true);
    try {
      const url = await onGetDownloadUrl(attachment.storage_path);
      if (url) {
        setViewing({ url, fileName: attachment.filename, fileType: attachment.content_type });
      }
    } finally {
      setIsLoadingUrl(false);
    }
  };

  return (
    <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors group">
      {/* File icon */}
      {getFileIcon(attachment.content_type, attachment.filename)}

      {/* Filename and size */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" title={attachment.filename}>
          {attachment.filename}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatFileSize(attachment.size)}
        </p>
      </div>

      {/* Quick download button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleDownload}
        disabled={isLoadingUrl}
        title="Download"
      >
        {isLoadingUrl ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
      </Button>

      {/* More actions dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={isLoadingUrl || isSaving}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleView} disabled={isLoadingUrl}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDownload} disabled={isLoadingUrl}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onSaveToDocuments(attachment)}
            disabled={isSaving}
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            {isSaving ? "Saving..." : "Save to Documents"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DocumentViewerDialog
        document={viewing}
        open={!!viewing}
        onOpenChange={(o) => !o && setViewing(null)}
      />
    </div>
  );
};
